'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  drawerProps
} from '@cat-ecom/pcc-components';
import { SavedList } from 'services/savedLists/types';
import { getSavedLists } from 'services/savedLists/actions';
import { getSavedListsFlags } from 'app/[lang]/[esite]/(core)/cart/utils/client-utils';
import { SAVED_LISTS_DRAWER_WIDTH } from 'app/[lang]/[esite]/(core)/cart/constants/common';
import { SavedListDrawerHeader } from './saved-list-drawer-header/saved-list-drawer-header';
import { SavedListControl } from './saved-list-control/saved-list-control';
import { SavedListTableWrapper } from './saved-list-table-wrapper';
import { RowSelectionState } from '@tanstack/react-table';
import { MyEquipmentList } from 'components/my-equipment/types';
import styles from './saved-list-drawer.module.scss';
import cx from 'classnames';
import { uploadedFiles } from '../create-list-modal/types';

type CreateActionResponse = {
  status: string;
  error: boolean;
};

export interface SavedListDrawerProps {
  drawerProps: drawerProps;
  closeDrawer: () => void;
  onActionDrawer?: (rowSelected: RowSelectionState) => void;
  onFailHandler?: () => void;
  isCspUser: boolean;
  drawerTitle: string;
  confirmBtnText: string;
  hideBackButton?: boolean;
  showCreateListBtn?: boolean;
  openButtonText?: string;
  isDealerUser?: boolean;
  savedList?: SavedList[];
  isInstantAccessUser?: boolean;
  isAssociateEquipmentToSavedList?: boolean;
  myEquipmentList?: MyEquipmentList[];
  selectedEquipmentIndex?: number;
  isError?: boolean;
  createAction?: (
    type: boolean,
    name: string,
    file?: File | uploadedFiles,
    equipment?: MyEquipmentList
  ) => Promise<CreateActionResponse>;
  savedListDetailFeatureFlag?: boolean;
  withMobileFixedActions?: boolean;
  showAllList?: boolean;
}

export const SavedListDrawer = ({
  drawerProps,
  closeDrawer,
  onActionDrawer,
  onFailHandler,
  isCspUser,
  drawerTitle,
  savedList,
  confirmBtnText,
  hideBackButton = false,
  savedListDetailFeatureFlag = false,
  showCreateListBtn = false,
  openButtonText,
  isDealerUser,
  isInstantAccessUser,
  isAssociateEquipmentToSavedList,
  myEquipmentList,
  selectedEquipmentIndex,
  isError,
  createAction,
  withMobileFixedActions = false,
  showAllList
}: Readonly<SavedListDrawerProps>) => {
  const isFirstLoad = useRef(true);
  const [params, setParams] = useState({
    isSelfShoppingList: !isCspUser,
    isFavorite: undefined
  });
  const [isLoading, setIsLoading] = useState(false);
  const [savedLists, setSavedLists] = useState<SavedList[]>();
  const [hasOwnLists, setHasOwnLists] = useState(false);
  const [hasFavoriteLists, setHasFavoriteLists] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  const getConditionalShoppingList = useCallback(
    async (params) => {
      if (savedList) {
        return savedList;
      } else {
        const { shoppingLists } = await getSavedLists(params);
        return shoppingLists;
      }
    },
    [savedList]
  );

  const fetchSavedLists = useCallback(
    async (params, shouldLoadFlags = false) => {
      setIsLoading(true);
      try {
        const shoppingLists = await getConditionalShoppingList(params);
        if (shouldLoadFlags) {
          let hasOwnSavedListsFlag = false;
          let hasFavoriteSavedListsFlag = false;

          if (shoppingLists) {
            const flags = getSavedListsFlags(shoppingLists);
            hasOwnSavedListsFlag = flags.hasOwnSavedListsFlag;
            hasFavoriteSavedListsFlag = flags.hasFavoriteSavedListsFlag;
          }

          setHasOwnLists(hasOwnSavedListsFlag);
          setHasFavoriteLists(hasFavoriteSavedListsFlag);
        }
        setSavedLists(shoppingLists);
        setFetchFailed(false);
      } catch {
        if (!isFirstLoad.current) {
          onFailHandler();
        }
        setSavedLists(null);
        closeDrawer();
        setFetchFailed(true);
      }
      setIsLoading(false);
      isFirstLoad.current = false;
    },
    [closeDrawer, onFailHandler, getConditionalShoppingList]
  );

  useEffect(() => {
    fetchSavedLists(params, isFirstLoad.current);
  }, [fetchSavedLists, params]);

  useEffect(() => {
    if (fetchFailed && drawerProps.isActive) {
      fetchSavedLists(params, true);
    }
  }, [drawerProps.isActive, fetchSavedLists, params, fetchFailed]);

  return (
    <Drawer
      {...drawerProps}
      data-testid="saved-list-drawer"
      position="right"
      disableBackdropClick
      hasBackdrop
      hideCloseButton={false}
      width={SAVED_LISTS_DRAWER_WIDTH}
    >
      <SavedListDrawerHeader
        title={drawerTitle}
        onClose={closeDrawer}
        hideBackButton={hideBackButton}
        showCreateListBtn={showCreateListBtn}
        openButtonText={openButtonText}
        isCspUser={isCspUser}
        isDealerUser={isDealerUser}
        isInstantAccessUser={isInstantAccessUser}
        isAssociateEquipmentToSavedList={isAssociateEquipmentToSavedList}
        myEquipmentList={myEquipmentList}
        selectedEquipmentIndex={selectedEquipmentIndex}
        isError={isError}
        createAction={createAction}
        withMobileFixedActions={withMobileFixedActions}
      />
      <DrawerBody>
        <div
          className={cx(
            'd-flex flex-column mt-md-2 align-items-sm-center align-items-md-start',
            styles['saved-list-drawer__body']
          )}
        >
          <SavedListControl
            params={params}
            hasOwnLists={hasOwnLists}
            hasFavoriteLists={hasFavoriteLists}
            setParams={setParams}
            isCspUser={isCspUser}
          />
          <SavedListTableWrapper
            isLoading={isLoading}
            savedLists={savedLists}
            isCspUser={isCspUser}
            closeDrawer={closeDrawer}
            onActionDrawer={onActionDrawer}
            confirmBtnText={confirmBtnText}
            savedListDetailFeatureFlag={savedListDetailFeatureFlag}
            showAllList={showAllList}
          />
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div id="saved-list-footer-container" />
        <div id="saved-list-footer-container-mobile" />
      </DrawerFooter>
    </Drawer>
  );
};
